import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../atoms";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { investorFounderAPI } from "@/services/api/investor.service";
import {
  BUSINESS_PLAN_SECTION_HINTS,
  BUSINESS_PLAN_SECTION_KEYS,
  BUSINESS_PLAN_SECTION_LABELS,
  type BusinessPlanSectionKey,
} from "@/constants/business-plan-sections";
import type { BusinessPlanContext, BusinessPlanSectionDraft } from "@/types/startup.types";

type SectionDraftForm = Record<BusinessPlanSectionKey, BusinessPlanSectionDraft>;

function emptySectionDrafts(): SectionDraftForm {
  return Object.fromEntries(
    BUSINESS_PLAN_SECTION_KEYS.map((key) => [key, { draft: "", guidance: "" }]),
  ) as SectionDraftForm;
}

function draftsFromContext(
  sectionDrafts?: BusinessPlanContext["sectionDrafts"],
): SectionDraftForm {
  const base = emptySectionDrafts();
  if (!sectionDrafts) return base;
  for (const key of BUSINESS_PLAN_SECTION_KEYS) {
    const entry = sectionDrafts[key];
    if (entry) {
      base[key] = {
        draft: entry.draft ?? "",
        guidance: entry.guidance ?? "",
      };
    }
  }
  return base;
}

function serializeDrafts(form: SectionDraftForm): BusinessPlanContext["sectionDrafts"] {
  const result: NonNullable<BusinessPlanContext["sectionDrafts"]> = {};
  for (const key of BUSINESS_PLAN_SECTION_KEYS) {
    const draft = form[key].draft?.trim();
    const guidance = form[key].guidance?.trim();
    if (draft || guidance) {
      result[key] = {
        ...(draft ? { draft } : {}),
        ...(guidance ? { guidance } : {}),
      };
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function countFilledSections(form: SectionDraftForm) {
  return BUSINESS_PLAN_SECTION_KEYS.filter(
    (key) => form[key].draft?.trim() || form[key].guidance?.trim(),
  ).length;
}

interface BusinessPlanSectionDraftsSettingsProps {
  startupId: string;
}

export const BusinessPlanSectionDraftsSettings: React.FC<
  BusinessPlanSectionDraftsSettingsProps
> = ({ startupId }) => {
  const queryClient = useQueryClient();
  const [openSection, setOpenSection] = useState<BusinessPlanSectionKey | null>(
    "executive_summary",
  );

  const { data, isLoading } = useQuery({
    queryKey: ["businessPlanContext", startupId],
    queryFn: () => investorFounderAPI.getBusinessPlanContext(startupId),
    enabled: Boolean(startupId),
  });

  const initialForm = useMemo(() => draftsFromContext(data?.sectionDrafts), [data?.sectionDrafts]);
  const [form, setForm] = useState<SectionDraftForm>(initialForm);
  const [savedForm, setSavedForm] = useState<SectionDraftForm>(initialForm);

  useEffect(() => {
    setForm(initialForm);
    setSavedForm(initialForm);
  }, [initialForm]);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(savedForm),
    [form, savedForm],
  );

  const saveMutation = useMutation({
    mutationFn: (sectionDrafts: BusinessPlanContext["sectionDrafts"]) =>
      investorFounderAPI.patchBusinessPlanContext(startupId, { sectionDrafts }),
    onSuccess: (_result, sectionDrafts) => {
      queryClient.invalidateQueries({ queryKey: ["businessPlanContext", startupId] });
      const next = draftsFromContext(sectionDrafts);
      setSavedForm(next);
      setForm(next);
    },
  });

  const filledCount = countFilledSections(form);

  const updateSection = (
    key: BusinessPlanSectionKey,
    field: keyof BusinessPlanSectionDraft,
    value: string,
  ) => {
    setForm((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  return (
    <SettingsSectionCard
      title="Section drafts & polish"
      description="Add your own wording for any section. On generate, the AI polishes your drafts into investor-ready business-plan language while keeping your facts and intent."
    >
      {isLoading ? (
        <div className="text-sm text-white/55">Loading...</div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-white/45">
            {filledCount > 0
              ? `${filledCount} section${filledCount === 1 ? "" : "s"} with your input — empty sections are generated from your burn chart data.`
              : "Leave sections empty to let the AI draft from your financial snapshot, or write rough notes and the AI will refine them."}{" "}
            For Team and Milestones, add founding team and roadmap under Startup settings
            if you have not configured a team roster or timeline events yet.
          </p>

          <div className="space-y-2">
            {BUSINESS_PLAN_SECTION_KEYS.map((key) => {
              const hasContent =
                Boolean(form[key].draft?.trim()) || Boolean(form[key].guidance?.trim());
              const isOpen = openSection === key;

              return (
                <div
                  key={key}
                  className="overflow-hidden rounded-[14px] border border-white/10 bg-white/[0.02]"
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
                    onClick={() => setOpenSection(isOpen ? null : key)}
                  >
                    <span className="text-sm font-semibold text-white/90">
                      {BUSINESS_PLAN_SECTION_LABELS[key]}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-white/45">
                      {hasContent ? (
                        <span className="rounded-full bg-brand/20 px-2 py-0.5 text-brand">
                          Your input
                        </span>
                      ) : null}
                      <span aria-hidden>{isOpen ? "−" : "+"}</span>
                    </span>
                  </button>

                  {isOpen ? (
                    <div className="space-y-3 border-t border-white/10 px-4 py-4">
                      {BUSINESS_PLAN_SECTION_HINTS[key] ? (
                        <p className="text-xs text-white/45">{BUSINESS_PLAN_SECTION_HINTS[key]}</p>
                      ) : null}
                      <label className="block space-y-1.5">
                        <span className="text-xs font-medium text-white/65">
                          Your draft (rough notes or full text)
                        </span>
                        <textarea
                          value={form[key].draft ?? ""}
                          onChange={(event) => updateSection(key, "draft", event.target.value)}
                          className="sh-select min-h-[120px]"
                          placeholder="Write what you want to say in your own words. The AI will polish tone and structure without changing your core message."
                        />
                      </label>
                      <label className="block space-y-1.5">
                        <span className="text-xs font-medium text-white/65">
                          Section guidance for AI (optional)
                        </span>
                        <textarea
                          value={form[key].guidance ?? ""}
                          onChange={(event) => updateSection(key, "guidance", event.target.value)}
                          className="sh-select min-h-[72px]"
                          placeholder="e.g. Keep under 200 words, emphasize enterprise pipeline, avoid market-size claims…"
                        />
                      </label>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end border-t border-white/10 pt-4">
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={saveMutation.isPending || !isDirty}
              onClick={() => saveMutation.mutate(serializeDrafts(form))}
            >
              {saveMutation.isPending ? "Saving..." : "Save section drafts"}
            </Button>
          </div>
        </div>
      )}
    </SettingsSectionCard>
  );
};
